var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { forwardRef } from 'react';
import { Listbox } from '@headlessui/react';
import clsx from 'clsx';
import { twMerge } from 'tailwind-merge';
import { useBackgroundContext } from '../BackgroundContext/index.js';
import { CheckIcon, SelectorIcon } from '../icons/index.js';
import { FloatingContextProvider, FloatingWrapper, useFloatingContext, } from '../utils/floating/index.js';
import { runIfFn } from '../utils/index.js';
import { useInputColors } from '../utils/styles/index.js';
import { SelectProvider, useSelect } from './SelectContext.js';
export const Select = (props) => {
    const { children, className, value, placeholder, items, invalid = false, disabled = false } = props, rest = __rest(props, ["children", "className", "value", "placeholder", "items", "invalid", "disabled"]);
    return (React.createElement(Listbox, Object.assign({}, rest, { disabled: disabled, value: value }), renderProps => {
        var _a;
        let content;
        if (items) {
            content = (React.createElement(React.Fragment, null,
                React.createElement(SelectButton, { placeholder: placeholder }, ((_a = items.find(item => value === item.value)) === null || _a === void 0 ? void 0 : _a.text) || null),
                React.createElement(SelectItems, null, items.map(item => (React.createElement(SelectItem, { key: item.value, value: item.value }, item.text))))));
        }
        else {
            content = runIfFn(children, renderProps);
        }
        return (React.createElement("div", { className: twMerge(clsx('relative isolate', className)) },
            React.createElement(SelectProvider, { open: renderProps.open, invalid: invalid, disabled: disabled },
                React.createElement(FloatingContextProvider, { placement: "bottom-start" }, content))));
    }));
};
const SelectButton = props => {
    const { children, className, placeholder, arrowsClassname } = props, rest = __rest(props, ["children", "className", "placeholder", "arrowsClassname"]);
    const { invalid, disabled } = useSelect();
    const { reference } = useFloatingContext();
    const { backgroundType } = useBackgroundContext();
    const { backgroundClsx, borderClsx, invalidClsx, disabledClsx, iconClsx, fontClsx, } = useInputColors({
        invalid,
        disabled,
    });
    const placeholderClsx = [
        !invalid && [
            backgroundType === 'main' &&
                'text-content-on-background-subdued opacity-60',
            backgroundType === 'surface' && 'text-content-subdued opacity-60',
            backgroundType === 'secondary' && 'text-content-primary opacity-60',
        ],
        invalid && 'text-action-destructive opacity-60',
    ];
    return (React.createElement("div", { ref: reference },
        React.createElement(Listbox.Button, Object.assign({ className: twMerge(clsx('relative w-full rounded-md shadow-sm ps-3 pe-10 py-2 text-start cursor-default', [
                'appearance-none',
                'text-base',
                fontClsx,
                backgroundClsx,
                borderClsx,
                [
                    'focus:outline-none focus-visible:ring ',
                    !invalid
                        ? 'focus-visible:ring-focused'
                        : 'focus-visible:ring-line-negative focus-visible:border-line-negative',
                ],
                invalid && invalidClsx,
                disabled && disabledClsx,
            ], className)) }, rest), () => (React.createElement(React.Fragment, null,
            React.createElement("span", { className: clsx('block truncate') }, children || (React.createElement("div", { className: clsx(placeholderClsx) }, placeholder || '\u00A0'))),
            React.createElement("span", { className: clsx('absolute inset-y-0 end-0 flex items-center pe-2 pointer-events-none', arrowsClassname) },
                React.createElement(SelectorIcon, { className: clsx('w-5 h-5', iconClsx), "aria-hidden": "true" })))))));
};
const SelectItems = forwardRef((props, ref) => {
    const { children, className } = props, rest = __rest(props, ["children", "className"]);
    const { open } = useSelect();
    return (React.createElement(FloatingWrapper, { open: open, sameWidth: true },
        React.createElement(Listbox.Options, Object.assign({ className: twMerge(clsx('w-full min-w-[160px] text-content-subdued bg-surface shadow-lg max-h-60 rounded-md py-1 text-base border border-line-subdued overflow-auto', 'focus-visible:ring-1 focus:outline-none', className)) }, rest, { ref: ref }), children)));
});
const SelectItem = props => {
    const { value, children, disabled, className } = props, rest = __rest(props, ["value", "children", "disabled", "className"]);
    return (React.createElement(Listbox.Option, Object.assign({ className: ({ active }) => twMerge(clsx(active
            ? 'text-content-on-primary bg-action-primary'
            : 'text-content', 'cursor-default select-none relative py-2 ps-3 pe-9', className)), value: value, disabled: disabled }, rest), ({ selected, active }) => (React.createElement(React.Fragment, null,
        React.createElement("span", { className: clsx(selected ? 'font-semibold' : 'font-normal', disabled ? 'opacity-50' : '', 'block truncate') }, runIfFn(children, {
            selected,
            active,
        })),
        selected ? (React.createElement("span", { className: clsx(active ? 'text-content-on-primary' : 'text-action-primary', 'absolute inset-y-0 end-0 flex items-center pe-4') },
            React.createElement(CheckIcon, { className: "h-5 w-5", "aria-hidden": "true" }))) : null))));
};
Select.Button = SelectButton;
Select.Items = SelectItems;
Select.Item = SelectItem;
//# sourceMappingURL=Select.js.map